import React, { useState, useContext, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Home,
  Lightbulb,
  Search,
  User,
  X,
  Menu,
  FileQuestion,
  Edit,
  LogOut,
  Settings,
  ChevronDown,
  Layers,
} from "lucide-react";
import toast from "react-hot-toast";
import { AppContext } from "../AppContext";
import logoImage from "../assets/logo-with-text.png";

const Header = () => {
  const { isLoggedIn, username, logout, showHeader } = useContext(AppContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Thêm refs cho nút user và menu dropdown
  const userMenuRef = useRef(null);
  const userButtonRef = useRef(null);

  const handleNav = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false); // Đóng dropdown khi chuyển trang
  };

  // Xử lý đóng menu khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Kiểm tra nếu click không phải là nút user VÀ click không phải là bên trong menu
      if (
        isUserMenuOpen &&
        userMenuRef.current &&
        userButtonRef.current &&
        !userMenuRef.current.contains(event.target) &&
        !userButtonRef.current.contains(event.target)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    // Chỉ thêm listener khi menu đang mở
    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup: Xóa listener khi component unmount hoặc menu đóng
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen]); // Chạy lại khi trạng thái menu thay đổi

  if (!showHeader) return null;

  const styleString = `
    @keyframes fadeInDown {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in-up { animation: fadeInUp 0.3s ease-out forwards; }
    .animate-fade-in-down { animation: fadeInDown 0.2s ease-out forwards; }
  `;

  return (
    <header className="bg-purple-700 text-white p-4 shadow-lg">
      <div className="container mx-auto flex justify-between items-center relative">
        {/* Logo */}
        <div
          className="flex items-center space-x-2 cursor-pointer group"
          onClick={() => handleNav("/")}
        >
          <img
            src={logoImage}
            alt="Logo"
            className="h-10 lg:h-12 transition-transform duration-300"
          />
        </div>

        {/* Search Bar */}
        <div className="flex-grow max-w-lg mx-2 lg:mx-8 hidden 2xl:block">
          <div className="relative">
            <input
              type="text"
              placeholder="Tìm kiếm"
              className="w-full pl-10 pr-4 py-2 rounded-full bg-white bg-opacity-20 text-white placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:bg-opacity-30 transition-all duration-300"
            />
            <Search
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-200"
            />
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden xl:flex items-center space-x-3 lg:space-x-6">
          <button
            onClick={() => handleNav("/")}
            className="hover:text-yellow-300 flex items-center text-sm lg:text-base"
          >
            <Home size={20} className="mr-1 lg:mr-2" /> Trang chủ
          </button>
          <button
            onClick={() => handleNav("/courses")}
            className="hover:text-yellow-300 flex items-center text-sm lg:text-base"
          >
            <BookOpen size={20} className="mr-1 lg:mr-2" /> Khóa Học
          </button>
          <button
            onClick={() => handleNav("/quiz")}
            className="hover:text-yellow-300 flex items-center text-sm lg:text-base"
          >
            <FileQuestion size={20} className="mr-1 lg:mr-2" /> Trắc nghiệm
          </button>
          <button
            onClick={() => handleNav("/practice")}
            className="hover:text-yellow-300 flex items-center text-sm lg:text-base"
          >
            <Edit size={20} className="mr-1 lg:mr-2" /> Giải đề
          </button>
          {/* Mục Chuyên đề mới */}
          <button
            onClick={() => handleNav("/topic")}
            className="hover:text-yellow-300 flex items-center text-sm lg:text-base"
          >
            <Layers size={20} className="mr-1 lg:mr-2" /> Chuyên đề
          </button>
          <button
            onClick={() => handleNav("/guide")}
            className="hover:text-yellow-300 flex items-center text-sm lg:text-base"
          >
            <Lightbulb size={20} className="mr-1 lg:mr-2" /> Hướng dẫn
          </button>

          {/* Auth */}
          {isLoggedIn ? (
            <div className="relative">
              <button
                ref={userButtonRef}
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="relative w-12 h-12 rounded-full flex items-center justify-center cursor-pointer"
              >
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                  <User size={20} color="#374151" />
                </div>
                <div
                  className={`absolute bottom-1 right-0 w-4 h-4 rounded-full bg-gray-300 flex items-center justify-center transition-transform duration-200 transform ${
                    isUserMenuOpen ? "rotate-180" : "rotate-0"
                  }`}
                >
                  <ChevronDown size={12} color="black" />
                </div>
              </button>
              {isUserMenuOpen && (
                <div
                  ref={userMenuRef}
                  className="absolute right-0 mt-2 w-max bg-white text-purple-800 rounded-md shadow-lg py-1 animate-fade-in-down z-50"
                >
                  <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-700">
                    Xin chào, {username}
                  </div>
                  <button
                    onClick={() => handleNav("/profile")}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center"
                  >
                    <Settings size={16} className="mr-2" /> Hồ sơ cá nhân
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={() => {
                      logout();
                      toast.success("Đăng xuất thành công!");
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-red-100 text-red-500 flex items-center"
                  >
                    <LogOut size={16} className="mr-2" /> Đăng xuất
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => handleNav("/login")}
              className="bg-white text-purple-800 font-semibold py-1 px-3 lg:py-2 lg:px-4 rounded-full shadow-md hover:bg-gray-200 flex items-center text-xs lg:text-sm"
            >
              <User size={16} className="mr-1 lg:mr-2" /> Đăng nhập
            </button>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <div className="xl:hidden flex items-center">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="text-white"
          >
            <Menu size={30} />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="xl:hidden fixed inset-0 bg-purple-800 bg-opacity-95 z-40 flex flex-col items-center justify-center space-y-6 animate-fade-in-up">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-4 right-4 text-white bg-red-600 rounded-full p-2 shadow-lg"
          >
            <X size={26} />
          </button>

          <button
            onClick={() => handleNav("/")}
            className="text-white text-xl font-bold hover:text-yellow-300 flex items-center"
          >
            <Home size={24} className="mr-1" /> Trang chủ
          </button>
          <button
            onClick={() => handleNav("/courses")}
            className="text-white text-xl font-bold hover:text-yellow-300 flex items-center"
          >
            <BookOpen size={24} className="mr-1" /> Khóa Học
          </button>
          <button
            onClick={() => handleNav("/quiz")}
            className="text-white text-xl font-bold hover:text-yellow-300 flex items-center"
          >
            <FileQuestion size={24} className="mr-1" /> Trắc nghiệm
          </button>
          <button
            onClick={() => handleNav("/practice")}
            className="text-white text-xl font-bold hover:text-yellow-300 flex items-center"
          >
            <Edit size={24} className="mr-1" /> Giải đề
          </button>
          {/* Mục Chuyên đề mới */}
          <button
            onClick={() => handleNav("/topic")}
            className="text-white text-xl font-bold hover:text-yellow-300 flex items-center"
          >
            <Layers size={24} className="mr-1" /> Chuyên đề
          </button>
          <button
            onClick={() => handleNav("/guide")}
            className="text-white text-xl font-bold hover:text-yellow-300 flex items-center"
          >
            <Lightbulb size={24} className="mr-1" /> Hướng dẫn
          </button>

          <div className="px-6 mt-4">
            {isLoggedIn ? (
              <div className="flex flex-col items-center space-y-4">
                <span className="block text-white font-semibold text-lg">
                  Xin chào, {username}
                </span>
                <button
                  onClick={() => {
                    handleNav("/profile");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-full shadow-lg text-xl flex items-center justify-center"
                >
                  <Settings size={24} className="mr-1" /> Hồ sơ cá nhân
                </button>
                <button
                  onClick={() => {
                    logout();
                    toast.success("Đăng xuất thành công!");
                  }}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-full shadow-lg text-xl flex items-center justify-center"
                >
                  <LogOut size={24} className="mr-1" /> Đăng xuất
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleNav("/login")}
                className="w-full bg-yellow-400 text-purple-800 font-semibold py-2 px-4 rounded-full shadow-lg hover:bg-yellow-500 text-xl flex items-center justify-center"
              >
                <User size={24} className="mr-1" /> Đăng nhập
              </button>
            )}
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: styleString }} />
    </header>
  );
};

export default Header;
